import React, { useEffect, useState, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider, useSelector } from 'react-redux';
import { Talkr } from 'talkr';
import { HashRouter, Routes, Route, useNavigate } from 'react-router-dom';
import store from './store';
import Menu from './components/menu';
import defaultLang from './en.json';
import ViewportSizes from './components/viewport';
import focusFix from './utils/scripts/focusFix';
import abTestGoal from './utils/scripts/abTest';
import getParameterFromURL from './utils/scripts/getParameterFromURL';

// Onboarding Female
import questions from './survey/questions';
import { questionsData } from './survey/questionsData';
import steps from './survey/steps';
import * as configs from './survey/configs';
import buildConfig from './survey/buildConfig';

// Onboarding Male
import questionsMan from './surveyMan/questionsMan';
import { questionsDataMan } from './surveyMan/questionsDataMan';
import stepsMan from './surveyMan/stepsMan';
import * as configsMan from './surveyMan/configsMan';
import buildConfigMan from './surveyMan/buildConfigMan';

// Pages
import Steps from './pages/steps';

import './index.scss';
import s from './app.module.scss';

const Result = React.lazy(() => import('./pages/result/reachYourWeight'));
const Loader = React.lazy(() => import('./pages/loader'));
const Contacts = React.lazy(() => import('./pages/contacts'));

if (getParameterFromURL('theme') === 'dark' || getParameterFromURL('planMan') === 'true') {
	document.querySelector('html').classList.add('darkTheme');
}

let userLang = window.navigator.language.slice(0, 2);

// locales ab tests
abTestGoal();

function App() {
	const showSteps = useSelector(state => state.global.showSteps);
	const initialGender = useSelector(state => state.global.initialGender);
	const showLoader = useSelector(state => state.global.showLoader);
	const stepsList = useSelector(state => state.steps.list);
	const config = useSelector(state => state.steps.config);
	const beforeAfterB = useSelector(state => state.steps.beforeAfterB);
	const xmasStartScreen = useSelector(state => state.steps.xmasStartScreen);
	const bigPhotoStartScreen = useSelector(state => state.steps.bigPhotoStartScreen);
	const ageGroupStartScreen = useSelector(state => state.steps.ageGroupStartScreen);
	const coloredOptionsStartScreen = useSelector(state => state.steps.coloredOptionsStartScreen);
	const zonesStartScreen = useSelector(state => state.steps.zonesStartScreen);
	const newYearStartScreen = useSelector(state => state.steps.newYearStartScreen);
	const planMan = useSelector(state => state.global.planMan);

	// Set Onboarding Gender
	const questionsType = planMan ? questionsMan : questions;
	const questionsDataType = planMan ? questionsDataMan : questionsData;
	const stepsType = planMan ? stepsMan : steps;
	const configsType = planMan ? configsMan : configs;
	const buildConfigType = planMan ? buildConfigMan : buildConfig;
	const planManGender = planMan ? 'male' : initialGender;
	const abTestResult = localStorage.getItem('abTestGoal');

	let configToApply;
	if (config && config === 'short' && abTestResult === 'B') {
		configToApply = configsType['shortB'];
	} else {
		configToApply = config && config in configsType ? configsType[config] : configsType.defaultConfig;
	}

	const beforeAfterC = useSelector(state => state.steps.beforeAfterC);
	const navigate = useNavigate();

	useEffect(() => {
		showLoader && navigate('/loader');
	}, [showLoader]);

	useEffect(() => {
		if (stepsList) return;

		questionsType.setup({
			questionsDataType,
		});

		focusFix();

		// Setup gender before steps.setup
		questionsType.setAnswer({
			questionName: 'gender',
			answerName: 'value',
			answerValue: planManGender,
		});

		function getCurrentStep() {
			if (beforeAfterB) {
				return 'before_after_b';
			}
			if (beforeAfterC) {
				return 'before_after_c';
			}
			if (xmasStartScreen) {
				return 'xmas_start_screen';
			}
			if (bigPhotoStartScreen) {
				return 'big_photo_start_screen';
			}
			if (ageGroupStartScreen) {
				return 'age_group_start_screen';
			}
			if (coloredOptionsStartScreen) {
				return 'colored_options_start_screen';
			}
			if (zonesStartScreen) {
				return 'zones_start_screen';
			}
			if (newYearStartScreen) {
				return 'new_year_start_screen';
			}
			return configToApply[0].name;
		}

		stepsType.setup({
			stepsConfig: buildConfigType({
				config: configToApply,
				configName: config,
			}),
			currentStep: getCurrentStep(),
			// currentStep: 'goal',
			stepsDetails: configs.stepsDetails,
		});

		questionsType.setAnswer({
			questionName: 'weight',
			answerName: 'value',
			answerValue: { value: '' },
		});
		questionsType.setAnswer({
			questionName: 'weight',
			answerName: 'units',
			answerValue: { value: 'lbs' },
		});
		questionsType.setAnswer({
			questionName: 'height',
			answerName: 'value',
			answerValue: { value: '' },
		});
		questionsType.setAnswer({
			questionName: 'height',
			answerName: 'units',
			answerValue: { value: 'ft' },
		});
		questionsType.setAnswer({
			questionName: 'target_weight',
			answerName: 'value',
			answerValue: { value: '' },
		});
		questionsType.setAnswer({
			questionName: 'target_weight',
			answerName: 'units',
			answerValue: { value: 'lbs' },
		});
	}, []);

	return <div className={s.app}>{showSteps && !showLoader && <Steps />}</div>;
}

function Localisations(props) {
	const forceLang = useSelector(state => state.global.forceLang);
	const [userLocale, setUserLocale] = useState(false);

	useEffect(() => {
		fetch(`./locales/${forceLang || userLang}.json`)
			.then(response => response.json())
			.then(data => {
				setUserLocale(data);

				window.locales = data;
				window.userLang = userLang;
			})
			.catch(() => {
				userLang = 'en';
				window.userLang = 'en';
				window.locales = defaultLang;
				setUserLocale(defaultLang);
			});

		window.defaultLocales = defaultLang;
	}, []);

	return (
		userLocale && (
			<Talkr languages={{ en: defaultLang, [userLang]: userLocale }} defaultLanguage="en" detectBrowserLanguage>
				{props.children}
			</Talkr>
		)
	);
}

const root = createRoot(document.getElementById('root')); // createRoot(container!) if you use TypeScript
root.render(
	<HashRouter>
		<Provider store={store}>
			<React.StrictMode>
				<Suspense fallback={<div style={{ margin: '0 auto' }}>loading...</div>}>
					<Localisations>
						<Routes>
							<Route path="/" element={<App />} />
							<Route path="/contacts" element={<Contacts />} />
							<Route path="/loader" element={<Loader />} />
							<Route path="/result" element={<Result />} />
						</Routes>
						<Menu />
						<ViewportSizes />
					</Localisations>
				</Suspense>
			</React.StrictMode>
		</Provider>
	</HashRouter>
);
